import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { showNoCameraFoundPopup, showNoCameraOrMicrophoneFound, showNoMicrophoneFoundPopup, showNotAllowedErrorPopup, } from '../components/popups/DeviceErrorPopup';
import { MediaStreamManager } from '../engine/helpers/MediaStreamManager';
/**
 * Starts, updates, and stops broadcaster's `MediaProducer`.
 */
export function useBroadcastUpdater({ broadcaster, enabled, mediaStream, }) {
    useEffect(function startMediaProducer() {
        if (!enabled ||
            mediaStream.getAudioTracks().length === 0 ||
            mediaStream.getVideoTracks().length === 0) {
            return () => { };
        }
        broadcaster?.startMediaProducer(mediaStream);
        return () => {
            broadcaster?.stopMediaProducer();
        };
    }, [broadcaster, mediaStream, enabled]);
}
/**
 * Manages broadcaster's mute/unmute state.
 */
export function useBroadcasterMute() {
    const [isAudioOn, setIsAudioOn] = useState(true);
    const [isVideoOn, setIsVideoOn] = useState(true);
    const toggleAudio = useCallback(() => {
        setIsAudioOn((s) => !s);
    }, []);
    const toggleVideo = useCallback(() => {
        setIsVideoOn((s) => !s);
    }, []);
    return { isAudioOn, isVideoOn, toggleAudio, toggleVideo };
}
/**
 * Toggles microphone/camera tracks depending on given options.
 */
export function useMediaStreamMuteEffect(mediaStream, options) {
    const { isAudioOn, isVideoOn } = options;
    useEffect(() => {
        for (const track of mediaStream.getTracks()) {
            if (track.kind === 'audio') {
                track.enabled = isAudioOn;
            }
            if (track.kind === 'video' && !isScreenSharingTrack(track)) {
                track.enabled = isVideoOn;
            }
        }
    }, [mediaStream, isAudioOn, isVideoOn]);
}
/**
 * Checks if given track carries a shared screen.
 */
export function isScreenSharingTrack(track) {
    return !!track.getSettings().displaySurface;
}
export function useVolumeController(broadcaster) {
    const volumeControllerRef = useRef(null);
    useEffect(() => {
        if (!broadcaster) {
            return;
        }
        function onVolumeChange(value) {
            volumeControllerRef.current?.setVolume(value);
        }
        broadcaster.on('audioVolume', onVolumeChange);
        return () => {
            broadcaster.off('audioVolume', onVolumeChange);
        };
    }, [broadcaster]);
    return volumeControllerRef;
}
/**
 * React hook that keeps track of the current main media stream (used for the broadcast).
 *
 * @example
 *
 * ```ts
 * const mediaStream = useMediaStream()
 * ```
 */
export function useMediaStream() {
    const [mediaStream, setMediaStream] = useState(MediaStreamManager.instance.mediaStream);
    useEffect(() => {
        MediaStreamManager.instance.on('mediaStreamChange', setMediaStream);
        setMediaStream(MediaStreamManager.instance.mediaStream);
        return () => {
            MediaStreamManager.instance.off('mediaStreamChange', setMediaStream);
        };
    }, []);
    return mediaStream;
}
/**
 * React hook that keeps track of current set of media device permissions granted by the user.
 *
 * @example
 *
 * ```ts
 * const { audio: canAccessMic } = useMediaPermissions()
 * ```
 */
export function useMediaPermissions() {
    const [permissions, setPermissions] = useState(MediaStreamManager.instance.permissions);
    useEffect(() => {
        MediaStreamManager.instance.on('permissionsChange', setPermissions);
        setPermissions(MediaStreamManager.instance.permissions);
        return () => {
            MediaStreamManager.instance.off('permissionsChange', setPermissions);
        };
    }, []);
    return permissions;
}
/**
 * React hook that returns a callback used for requesting media device permissions.
 *
 * It deals with certain edge cases:
 * - Shows the "No camera found" popup if requested is the camera permission exclusively and
 * no camera is present in the system.
 * - Shows the "No microphone found" popup if requested is the microphone permission exclusively
 * and no microphone is present in the system.
 * - Shows the "Microphone and camera blocked" popup (or its variation) if the user have denied
 * permissions "for good" in the past.
 *
 * It fails with a console warning in other exceptional situations.
 *
 * @example
 *
 * ```ts
 * const requestPermissions = useRequestMediaPermissionsCallback()
 * // …
 * requestPermissions({ audio: true, video: false }) // request mic only
 * ```
 */
export function useRequestMediaPermissionsCallback() {
    return useCallback(({ audio, video }) => {
        void (async () => {
            try {
                const presence = await MediaStreamManager.instance.getDevicePresenceSnapshot();
                if (audio && !video && !presence.audio) {
                    showNoMicrophoneFoundPopup();
                    return;
                }
                if (video && !audio && !presence.video) {
                    showNoCameraFoundPopup();
                    return;
                }
                if (audio && !presence.audio && video && !presence.video) {
                    showNoCameraOrMicrophoneFound();
                    return;
                }
                const requestedAt = performance.now();
                try {
                    await MediaStreamManager.instance.requestPermission({
                        audio: audio && presence.audio,
                        video: video && presence.video,
                    });
                }
                catch (e) {
                    if (e instanceof Error && e.name === 'NotAllowedError') {
                        if (performance.now() - requestedAt < 100) {
                            showNotAllowedErrorPopup({ audio, video });
                        }
                        return;
                    }
                    console.error('MediaStreamManager.instance.requestPermission failed', e);
                }
            }
            catch (e) {
                console.warn('MediaStreamManager.instance.getDevicePresenceSnapshot failed', e);
            }
        })();
    }, []);
}
/**
 * React hook that returns a callback to be used for display surface selection (enable/disable
 * screen sharing).
 *
 * @example
 *
 * ```ts
 * const setDisplaySurface = useSetDisplaySurfaceCallback()
 * // ...
 * setDisplaySurface('monitor')
 * ```
 */
export function useSetDisplaySurfaceCallback() {
    return useCallback((value) => {
        void (async () => {
            try {
                await MediaStreamManager.instance.engageDisplaySurface(value);
            }
            catch (e) {
                if (e instanceof Error && e.name === 'NotAllowedError') {
                    return;
                }
                console.warn('Failed to share screen', e);
            }
        })();
    }, []);
}
/**
 * React hook that returns information on media device presence (is mic/cam present in the system).
 *
 * It relies on the `devicechange` event and comes with a key limitation:
 * - It only starts automatically updating after the user has granted permission
 *  to access the microphone, camera, or (ideally) both.
 * - Before permission is granted, device changes will not trigger updates.
 *
 * @example
 *
 * ```ts
 * const { audio: micPresent } = useDevicePresence()
 * ```
 */
export function useDevicePresence() {
    const [presence, setPresence] = useState(MediaStreamManager.instance.devicePresence);
    useEffect(() => {
        MediaStreamManager.instance.on('devicePresenceChange', setPresence);
        setPresence(MediaStreamManager.instance.devicePresence);
        return () => {
            MediaStreamManager.instance.off('devicePresenceChange', setPresence);
        };
    }, []);
    return presence;
}
/**
 * React hook that returns the currently selected media input devices:
 * - Returns the active microphone and camera (if selected).
 * - Does not request permissions or ensure device availability on its own.
 *
 * @example
 *
 * ```ts
 * const { audio: microphone } = useSelectedDevices()
 * ```
 */
export function useSelectedDevices() {
    const [devices, setDevices] = useState(MediaStreamManager.instance.selectedDevices);
    useEffect(() => {
        function onSelectedDevicesChange() {
            setDevices(MediaStreamManager.instance.selectedDevices);
        }
        MediaStreamManager.instance.on('selectedDevicesChange', onSelectedDevicesChange);
        onSelectedDevicesChange();
        return () => {
            MediaStreamManager.instance.off('selectedDevicesChange', onSelectedDevicesChange);
        };
    }, []);
    return devices;
}
/**
 * React hook that returns audio and video media devices.
 *
 * @example
 *
 * ```ts
 * const { video: cameras } = useDevices()
 * ```
 */
export function useDevices() {
    const [devices, setDevices] = useState(MediaStreamManager.instance.devices);
    useEffect(() => {
        function onDeviceListChange() {
            setDevices(MediaStreamManager.instance.devices);
        }
        MediaStreamManager.instance.on('deviceListChange', onDeviceListChange);
        onDeviceListChange();
        return () => {
            MediaStreamManager.instance.off('deviceListChange', onDeviceListChange);
        };
    }, []);
    return devices;
}
/**
 * React hook that determines if given media stream contains a "display" media video track.
 *
 * @example
 *
 * ```ts
 * const isScreenShare = useIsSharingScreen(mediaStream)
 * ```
 */
export function useIsSharingScreen(mediaStream) {
    return useMemo(() => {
        for (const track of mediaStream.getVideoTracks()) {
            if (isScreenSharingTrack(track)) {
                return true;
            }
        }
        return false;
    }, [mediaStream]);
}
/**
 * React hook that determines if any video feed in given media stream comes from the
 * user-facing camera.
 *
 * @example
 *
 * ```ts
 * const isUsingFrontCamera = useIsUsingFrontCamera(mediaStream)
 * ```
 */
export function useIsUsingFrontCamera(mediaStream) {
    return useMemo(() => {
        for (const track of mediaStream.getVideoTracks()) {
            if (isUserFacingCameraTrack(track)) {
                return true;
            }
        }
        return false;
    }, [mediaStream]);
}
/**
 * Checks if track's facing mode indicates user-facing camera. If facing
 * mode isn't defined it infers the mode from other track properties.
 */
export function isUserFacingCameraTrack(track) {
    return (!isScreenSharingTrack(track) &&
        (track.getSettings().facingMode === 'user' ||
            /facetime hd camera|integrated/i.test(track.label)));
}
/**
 * React hook that returns a callback used for selecting a media device.
 *
 * @example
 *
 * ```ts
 * const selectDevice = useSelectMediaDeviceCallback()
 * // …
 * selectDevice('audio', 'DEVICEID')
 * ```
 */
export function useSelectMediaDeviceCallback() {
    return useCallback((kind, deviceId) => {
        void (async () => {
            try {
                await MediaStreamManager.instance.selectDeviceById(kind, deviceId);
            }
            catch (e) {
                console.warn('Failed to select device', kind, deviceId, e);
            }
        })();
    }, []);
}
/**
 * React hook that determines what kinds of tracks a given media stream contains.
 *
 * @example
 *
 * ```ts
 * const { video: hasVideo } = useMediaStreamContent(mediaStream)
 * ```
 */
export function useMediaStreamContent(mediaStream) {
    return useMemo(() => {
        return {
            audio: mediaStream.getAudioTracks().length > 0,
            video: mediaStream.getVideoTracks().length > 0,
        };
    }, [mediaStream]);
}
